import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { getBrowserSupport } from '~/utils/browserSupport';
import { enqueueSnackbar } from '~/utils/notifications';

/**
 * Announces, once per session, the capabilities this browser is missing that
 * leave the viewer usable but limited — a CPU rasteriser, a texture ceiling
 * below a routine volume, the float formats PET display and GPU volume
 * calculation rely on.
 *
 * Blocking gaps never reach here; they replace the application with
 * `UnsupportedEnvironmentPage` before it mounts. What is left are effects the
 * user would otherwise have to infer from slow rendering or from values that
 * quietly lost precision.
 *
 * Renders nothing.
 */
const BrowserSupportWarning = () => {
    const intl = useIntl();

    useEffect(() => {
        const degraded = getBrowserSupport().issues.filter(
            (issue) => issue.severity === 'degraded'
        );
        if (degraded.length === 0) {
            return;
        }

        const message = [
            intl.formatMessage({ id: 'browserSupport.degraded' }),
            ...degraded.map((issue) =>
                intl.formatMessage({
                    id: `browserSupport.issue.${issue.code}`,
                })
            ),
        ].join(' ');

        enqueueSnackbar(message, { variant: 'warning' });
    }, [intl]);

    return null;
};

export default BrowserSupportWarning;
